import { PublicUserGq, PublicUserGqProps } from '../../users/models/PublicUserGq';
import {
  TeamProjectAccessGq,
  TeamProjectAccessGqProps,
} from '../../users/models/TeamProjectAccessGq';
import { ContentChatGptRequestGq, ContentChatGptRequestGqProps } from './ContentChatGptRequestGq';
import { CurrencyConversionGq, CurrencyConversionGqProps } from './CurrencyConversionGq';
import { DomainGq, DomainGqProps } from './DomainGq';
import { MultiProjectGq, MultiProjectGqProps } from './MultiProjectGq';
import { ProjectDuplicateStatusGq } from './ProjectDuplicateStatusGq';
import {
  ProjectSerpTrackingRequestGq,
  ProjectSerpTrackingRequestGqProps,
} from './ProjectSerpTrackingRequestGq';
import { ProjectTrackingFrequencyGq } from './ProjectTrackingFrequencyGq';
import { ProjectTypeGq } from './ProjectTypeGq';
import { TagGroupGq, TagGroupGqProps } from './TagGroupGq';

export interface ProjectGq {
  // Project data
  readonly id: number;
  readonly exampleProject: boolean;
  readonly exampleProjectSource: boolean;
  readonly type: ProjectTypeGq;
  readonly tagImportProject: boolean;
  readonly analysisColorPaletteId: number | null;
  readonly negativeTermsSource?: string | null; // Not included in the big list
  readonly useAutomaticDomains: boolean;

  readonly name: string | null;
  readonly clientName: string | null;
  readonly clientWebsite: string | null;
  readonly currency: string | null;
  readonly currencyConversion: CurrencyConversionGq;
  readonly currencyConversionRate: number;

  readonly progressKeywordCount: number;
  readonly projectHasKeywordRequests: boolean;
  readonly progressHasTags: boolean;
  readonly progressHasSerp: boolean;
  readonly progressHasContentPlan: boolean;

  readonly defaultLocationId?: number;
  readonly defaultLanguageId?: number;
  readonly defaultLocationName?: string;
  readonly defaultLanguageName?: string;

  readonly defaultMinSearchVolume: number;
  readonly defaultIncludeMonths: boolean;

  // Processing status
  readonly copyInProgress: boolean;
  readonly copyFailed: boolean;
  readonly scrapeCopyInProgress: boolean;
  readonly translateInProgress: boolean;
  readonly translated: boolean;
  readonly hasEnglishTranslation: boolean;
  readonly duplicateStatus: ProjectDuplicateStatusGq;

  // Serp
  readonly serpLocationId?: number;
  readonly serpLanguageId?: number;
  readonly serpLocationName?: string;
  readonly serpLanguageName?: string;
  readonly serpAutomaticDomainComputed?: string;
  readonly serpTriggered?: string;
  readonly domainSerpTriggered?: string;
  readonly serpTrackingOn?: string;
  readonly serpTrackingFrequency?: ProjectTrackingFrequencyGq;
  readonly projectSerpTrackingRequests?: ProjectSerpTrackingRequestGq[];

  // Transaction state
  readonly createdTime: string | null;
  readonly createdById: number | null;
  readonly createdBy: PublicUserGq | null;
  readonly archivedTime: string | null;
  readonly archivedById: number | null;
  readonly archivedBy: PublicUserGq | null;

  // Owned related data
  readonly tagGroups: TagGroupGq[] | null;
  readonly domains: DomainGq[];
  readonly grantedTeamAccess: TeamProjectAccessGq[] | null;

  // Month data
  readonly longMonthDisabled: boolean;
  readonly keywordMonth12Start?: string;
  readonly keywordMonth12End?: string;
  readonly keywordMonth24Start?: string;
  readonly keywordMonth24End?: string;
  readonly keywordMonth36Start?: string;
  readonly keywordMonth36End?: string;

  // Multi project
  readonly childProjects: MultiProjectGq[];
  readonly contentChatGptRequests?: ContentChatGptRequestGq[];

  // Access
  readonly accessUserCanEdit: boolean;
  readonly accessUserCanArchive: boolean;
}

export const ProjectGqPropsLimited = `
  id
  exampleProject
  exampleProjectSource
  useAutomaticDomains
  type
  tagImportProject
  analysisColorPaletteId
  
  name
  clientName
  clientWebsite
  currency
  currencyConversion { ${CurrencyConversionGqProps} }
  currencyConversionRate

  progressKeywordCount
  projectHasKeywordRequests
  progressHasTags
  progressHasSerp
  progressHasContentPlan
  
  defaultLocationId
  defaultLanguageId
  defaultLocationName
  defaultLanguageName
  
  defaultMinSearchVolume
  defaultIncludeMonths

  copyInProgress
  copyFailed
  scrapeCopyInProgress
  translateInProgress
  translated
  hasEnglishTranslation
  duplicateStatus
  longMonthDisabled
  
  serpLocationId
  serpLanguageId
  serpLocationName
  serpLanguageName
  serpAutomaticDomainComputed
  serpTriggered
  domainSerpTriggered
  serpTrackingFrequency
  serpTrackingOn

  createdTime
  createdById
  createdBy { ${PublicUserGqProps} }
  archivedTime 
  archivedById
  archivedBy { ${PublicUserGqProps} }
  
  tagGroups { ${TagGroupGqProps} }
  domains { ${DomainGqProps} }
  grantedTeamAccess {${TeamProjectAccessGqProps}}

  longMonthDisabled
  keywordMonth12Start
  keywordMonth12End
  keywordMonth24Start
  keywordMonth24End
  keywordMonth36Start
  keywordMonth36End

  childProjects { ${MultiProjectGqProps} }
  projectSerpTrackingRequests { ${ProjectSerpTrackingRequestGqProps} }
  accessUserCanEdit
  accessUserCanArchive
`;

// This data might be really large - we don't want it in the long list
export const ProjectGqProps = `
  negativeTermsSource
  contentChatGptRequests { ${ContentChatGptRequestGqProps} }
  ${ProjectGqPropsLimited}
`;
